import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { enterDemoMode, exitDemoMode, resetDemoMode, updateDemoBranding } from '../api/axios';
import { useAuth } from '../context/AuthContext';
import type { SchoolCode } from '../types';
import { SCHOOLS } from '../types';

export const DevDemoPage: React.FC = () => {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [names, setNames] = useState<Record<SchoolCode, string>>({
    KBA: user?.demo_branding?.find((row) => row.school === 'KBA')?.display_name || `${SCHOOLS.KBA.name} (Demo)`,
    TLC: user?.demo_branding?.find((row) => row.school === 'TLC')?.display_name || `${SCHOOLS.TLC.name} (Demo)`,
  });
  const [logos, setLogos] = useState<Record<SchoolCode, File | null>>({ KBA: null, TLC: null });

  const applyBranding = (demoMode: boolean, branding?: NonNullable<typeof user>['demo_branding']) => {
    setUser((current) => current ? { ...current, demo_mode: demoMode, demo_branding: branding ?? current.demo_branding } : current);
  };

  const run = async (action: 'enter' | 'reset' | 'exit') => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      if (action === 'enter') {
        const result = await enterDemoMode();
        applyBranding(true, result.branding);
        navigate('/admin', { replace: true });
        return;
      }
      if (action === 'reset') {
        const result = await resetDemoMode();
        applyBranding(true, result.branding);
        setMessage(result.message);
        return;
      }
      await exitDemoMode();
      applyBranding(false);
      setMessage('Demo mode is off. Live data is what you will see next.');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'That demo action failed.');
    } finally {
      setBusy(false);
    }
  };

  const saveBranding = async (school: SchoolCode) => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await updateDemoBranding({
        school,
        display_name: names[school],
        logo: logos[school],
      });
      applyBranding(true, result.branding);
      setMessage(`${school} demo name and logo were saved.`);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not save the demo branding.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa', padding: 32 }}>
      <div style={{ maxWidth: 760, margin: '0 auto', background: '#fff', borderRadius: 16, padding: 28, border: '1px solid #e5e7eb' }}>
        <h1 style={{ marginTop: 0 }}>Demo mode</h1>
        <p style={{ color: '#4b5563', lineHeight: 1.6 }}>
          Signed in as {user?.login_id || 'Dev'}. Demo mode uses a separate copy of KBA and TLC.
          Entering it does not change live students, fees, or settings.
        </p>
        <p style={{ fontWeight: 600 }}>{user?.demo_mode ? 'Demo is on.' : 'Demo is off.'}</p>
        {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
        {message && <p style={{ color: '#047857' }}>{message}</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!user?.demo_mode && (
            <button type="button" disabled={busy} onClick={() => void run('enter')} style={primaryButton}>
              Enter demo
            </button>
          )}
          {user?.demo_mode && (
            <>
              <button type="button" disabled={busy} onClick={() => navigate('/admin')} style={primaryButton}>
                Open demo admin
              </button>
              <button type="button" disabled={busy} onClick={() => void run('reset')} style={primaryButton}>
                Reset demo
              </button>
              <button type="button" disabled={busy} onClick={() => void run('exit')} style={{ ...primaryButton, background: '#b91c1c' }}>
                Exit demo
              </button>
            </>
          )}
          <button type="button" onClick={() => void logout()} style={{ ...primaryButton, background: '#6b7280' }}>
            Sign out
          </button>
        </div>

        <h2 style={{ fontSize: 18, marginTop: 28 }}>Rebrand the demo schools</h2>
        <p style={{ color: '#4b5563' }}>
          Change the name and logo used while demo mode is on. Live school names stay as they are.
        </p>
        {(['KBA', 'TLC'] as SchoolCode[]).map((school) => {
          const currentLogo = user?.demo_branding?.find((row) => row.school === school)?.logo_url;
          return (
            <div key={school} style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 16 }}>
              <h3 style={{ margin: '0 0 8px' }}>{school}</h3>
              {currentLogo && (
                <img src={currentLogo} alt="" style={{ height: 48, objectFit: 'contain', marginBottom: 8 }} />
              )}
              <label style={{ display: 'block', marginBottom: 8 }}>
                Display name
                <input
                  value={names[school]}
                  onChange={(e) => setNames((prev) => ({ ...prev, [school]: e.target.value }))}
                  style={{ display: 'block', width: '100%', marginTop: 4, padding: 10, borderRadius: 8, border: '1px solid #d1d5db' }}
                />
              </label>
              <label style={{ display: 'block', marginBottom: 8 }}>
                Logo
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogos((prev) => ({ ...prev, [school]: e.target.files?.[0] ?? null }))}
                />
              </label>
              <button type="button" disabled={busy || !user?.demo_mode} onClick={() => void saveBranding(school)} style={primaryButton}>
                Save {school} branding
              </button>
              {!user?.demo_mode && <p style={{ color: '#6b7280' }}>Enter demo mode before saving a name or logo.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const primaryButton: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1e3a5f',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontWeight: 600,
  cursor: 'pointer',
};
